'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Users, ChevronDown, Loader2, CheckCircle, Eye, Crown, Shield, UserCheck, User, Search, X,  } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { userProfilesService, type UserProfile, type UserRoleLevel } from '@/lib/supabase/services';

const ROLE_CONFIG: Record<UserRoleLevel, {
  label: string;
  color: string;
  icon: React.ReactNode;
  description: string;
  permissions: string[];
}> = {
  'مدخل_بيانات': {
    label: 'مدخل بيانات',
    color: 'bg-muted/50 text-muted-foreground border-border',
    icon: <User size={14} />,
    description: 'صلاحيات محدودة — إدخال بيانات الزوار فقط',
    permissions: ['عرض سجل الزوار', 'تسجيل دخول الزوار', 'البحث عن الزوار'],
  },
  'مشرف': {
    label: 'مشرف',
    color: 'bg-info/15 text-info border-info/30',
    icon: <Shield size={14} />,
    description: 'إشراف على العمليات اليومية وإدارة الورديات',
    permissions: ['كل صلاحيات مدخل البيانات', 'إدارة أفراد الأمن والورديات', 'إدارة المواد الخام والمنتجات', 'تعديل بيانات الزوار', 'عرض التقارير الأساسية'],
  },
  'مدير_اداري': {
    label: 'مدير إداري',
    color: 'bg-warning/15 text-warning border-warning/30',
    icon: <UserCheck size={14} />,
    description: 'إدارة شاملة مع صلاحيات تعديل الإعدادات',
    permissions: ['كل صلاحيات المشرف', 'إدارة صلاحيات المستخدمين', 'عرض جميع التقارير', 'تصدير البيانات', 'إدارة القائمة السوداء'],
  },
  'مدير_تنفيذي': {
    label: 'مدير تنفيذي',
    color: 'bg-accent/15 text-accent border-accent/30',
    icon: <Crown size={14} />,
    description: 'صلاحيات كاملة على جميع وحدات النظام',
    permissions: ['كل الصلاحيات', 'إدارة المستخدمين والأدوار', 'لوحة التحكم الشاملة', 'التقارير التنفيذية', 'إعدادات النظام'],
  },
};

const ROLE_ORDER: UserRoleLevel[] = ['مدخل_بيانات', 'مشرف', 'مدير_اداري', 'مدير_تنفيذي'];

export default function PermissionsContent() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<UserRoleLevel | 'الكل'>('الكل');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await userProfilesService.getAll();
      setUsers(data);
    } catch (err: any) {
      toast.error('فشل تحميل المستخدمين: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleRoleChange = async (userId: string, newRole: UserRoleLevel) => {
    setUpdatingId(userId);
    try {
      await userProfilesService.updateRole(userId, newRole);
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, role: newRole } : u));
      toast.success(`تم تحديث الصلاحية إلى "${ROLE_CONFIG[newRole].label}"`);
    } catch (err: any) {
      toast.error('فشل تحديث الصلاحية: ' + err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = users.filter((u) => {
    const matchSearch = !searchQuery || u.fullName.includes(searchQuery) || u.email.includes(searchQuery);
    const matchRole = filterRole === 'الكل' || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const roleCounts = ROLE_ORDER.reduce((acc, role) => {
    acc[role] = users.filter((u) => u.role === role).length;
    return acc;
  }, {} as Record<UserRoleLevel, number>);

  return (
    <div className="min-h-screen bg-background p-4 xl:p-6" dir="rtl">
      <Toaster position="bottom-right" richColors />

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-warning/15 border border-warning/30 flex items-center justify-center">
          <Lock size={20} className="text-warning" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-foreground">نظام الصلاحيات والأدوار</h1>
          <p className="text-sm text-muted-foreground">إدارة مستويات الوصول للمستخدمين</p>
        </div>
      </div>

      {/* Role cards overview */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
        {ROLE_ORDER.map((role) => {
          const cfg = ROLE_CONFIG[role];
          return (
            <div key={role} className={`bg-card border rounded-xl p-4 ${cfg.color.includes('border') ? cfg.color.split(' ').find((c) => c.startsWith('border')) : 'border-border'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`p-1.5 rounded-lg ${cfg.color}`}>{cfg.icon}</span>
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{cfg.label}</span>
              </div>
              <p className="text-3xl font-bold text-foreground tabular-nums">{roleCounts[role] || 0}</p>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{cfg.description}</p>
            </div>
          );
        })}
      </div>

      {/* Permissions matrix */}
      <div className="bg-card border border-border rounded-2xl p-5 mb-5">
        <h2 className="font-bold text-foreground mb-4 flex items-center gap-2">
          <Eye size={16} className="text-accent" />
          مصفوفة الصلاحيات
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">الصلاحية</th>
                {ROLE_ORDER.map((role) => (
                  <th key={role} className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground whitespace-nowrap">
                    {ROLE_CONFIG[role].label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'عرض سجل الزوار', roles: ['مدخل_بيانات', 'مشرف', 'مدير_اداري', 'مدير_تنفيذي'] },
                { label: 'تسجيل دخول/خروج الزوار', roles: ['مدخل_بيانات', 'مشرف', 'مدير_اداري', 'مدير_تنفيذي'] },
                { label: 'إدارة أفراد الأمن والورديات', roles: ['مشرف', 'مدير_اداري', 'مدير_تنفيذي'] },
                { label: 'إدارة المواد الخام والمنتجات', roles: ['مشرف', 'مدير_اداري', 'مدير_تنفيذي'] },
                { label: 'عرض التقارير الشاملة', roles: ['مدير_اداري', 'مدير_تنفيذي'] },
                { label: 'إدارة صلاحيات المستخدمين', roles: ['مدير_اداري', 'مدير_تنفيذي'] },
                { label: 'تصدير البيانات', roles: ['مدير_اداري', 'مدير_تنفيذي'] },
                { label: 'إعدادات النظام الكاملة', roles: ['مدير_تنفيذي'] },
              ].map((row) => (
                <tr key={row.label} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                  <td className="py-2.5 px-3 text-sm text-foreground">{row.label}</td>
                  {ROLE_ORDER.map((role) => (
                    <td key={role} className="py-2.5 px-3 text-center">
                      {row.roles.includes(role) ? (
                        <CheckCircle size={16} className="text-success mx-auto" />
                      ) : (
                        <X size={16} className="text-muted-foreground/40 mx-auto" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Users list */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="بحث بالاسم أو البريد الإلكتروني..."
              className="w-full bg-input border border-border rounded-xl pr-9 pl-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {(['الكل', ...ROLE_ORDER] as (UserRoleLevel | 'الكل')[]).map((role) => (
              <button
                key={role}
                onClick={() => setFilterRole(role)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${filterRole === role ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
              >
                {role === 'الكل' ? 'الكل' : ROLE_CONFIG[role as UserRoleLevel].label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="text-accent animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Users size={36} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">لا يوجد مستخدمون</p>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {filtered.map((user) => {
              const cfg = ROLE_CONFIG[user.role];
              const isUpdating = updatingId === user.id;
              return (
                <div key={user.id} className="flex items-center gap-4 px-5 py-4 hover:bg-muted/20 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                    <User size={18} className="text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground text-sm truncate">{user.fullName || 'مستخدم'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${cfg.color}`}>
                      {cfg.icon}
                      {cfg.label}
                    </span>
                    <div className="relative">
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value as UserRoleLevel)}
                        disabled={isUpdating}
                        className="appearance-none bg-muted border border-border rounded-lg px-3 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring pr-7 disabled:opacity-50 cursor-pointer"
                      >
                        {ROLE_ORDER.map((role) => (
                          <option key={role} value={role}>{ROLE_CONFIG[role].label}</option>
                        ))}
                      </select>
                      {isUpdating ? (
                        <Loader2 size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-accent animate-spin" />
                      ) : (
                        <ChevronDown size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
